export enum MsdAiResultType {
  imgAnalysis = 'imgAnalysis',
  imgLegendAnalysis = 'imgLegendAnalysis',
  legendAnalysis = 'legendAnalysis',
  ingestas = 'ingestas',
}

export interface MsdAiResultSchema {
  uid: string;
  type: MsdAiResultType;
  title: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  content: any | null;
  next: MsdAiResultType | null;
}

export interface MsdAiResult {
  uid: string;
  type: MsdAiResultType;
  title: string | undefined;
  inputTokens: number | undefined;
  outputTokens: number | undefined;
  content: any | undefined;
  next: MsdAiResultType | undefined;
}

export class MsdAiResultUtil {
  public static fromSchemas(
    schema: MsdAiResultSchema[],
  ): MsdAiResult[] | undefined {
    if (schema) {
      return schema.map((m) => MsdAiResultUtil.fromSchema(m));
    }
    return undefined;
  }

  public static toSchemas(
    entities: MsdAiResult[] | undefined,
  ): MsdAiResultSchema[] | null {
    if (entities) {
      const results: MsdAiResultSchema[] = [];
      for (const entity of entities) {
        const schema = MsdAiResultUtil.toSchema(entity);
        if (schema) {
          results.push(schema);
        }
      }
      return results;
    }
    return null;
  }

  public static copy(origin: MsdAiResult): MsdAiResult {
    return {
      uid: origin.uid,
      type: origin.type,
      title: origin.title ?? undefined,
      inputTokens: origin.inputTokens ?? undefined,
      outputTokens: origin.outputTokens ?? undefined,
      content: origin.content
        ? this.copyContentObject(origin.content)
        : undefined,
      next: origin.next ?? undefined,
    } as MsdAiResult;
  }

  private static copyContentObject(originContent: any): any {
    if (typeof originContent === 'string') {
      return originContent;
    } else {
      return {
        energy: {
          unit: originContent.energy.unit,
          value: originContent.energy.value,
        },
        fibers: {
          unit: originContent.fibers.unit,
          value: originContent.fibers.value,
        },
        salt: {
          unit: originContent.salt.unit,
          value: originContent.salt.value,
        },
        sugar: originContent.sugar
          ? {
              unit: originContent.sugar?.unit ?? undefined,
              value: originContent.sugar?.value ?? undefined,
            }
          : undefined,
        proteins: {
          unit: originContent.proteins.unit,
          value: originContent.proteins.value,
          ratio: originContent.proteins.ratio,
        },
        glucids: {
          unit: originContent.glucids.unit,
          value: originContent.glucids.value,
          ratio: originContent.glucids.ratio,
        },
        lipids: {
          unit: originContent.lipids.unit,
          value: originContent.lipids.value,
          ratio: originContent.lipids.ratio,
        },
        error: originContent.error,
      };
    }
  }

  public static fromSchema(schema: MsdAiResultSchema): MsdAiResult {
    return {
      uid: schema.uid,
      type: schema.type,
      title: schema.title ?? undefined,
      inputTokens: schema.inputTokens ?? undefined,
      outputTokens: schema.outputTokens ?? undefined,
      content: schema.content ?? undefined,
      next: schema.next ?? undefined,
    } as MsdAiResult;
  }

  public static toSchema(entity: MsdAiResult): MsdAiResultSchema | null {
    if (entity) {
      return <MsdAiResultSchema>{
        uid: entity.uid,
        type: entity.type,
        title: entity.title ?? null,
        inputTokens: entity.inputTokens ?? null,
        outputTokens: entity.outputTokens ?? null,
        content: MsdAiResultUtil.toContentSchema(entity.content),
        next: entity.next ?? null,
      };
    }
    return null;
  }

  public static toContentSchema(content: any): any {
    if (content) {
      if (typeof content === 'object') {
        for (const prop in content) {
          content[prop] = content[prop] ?? null;
        }
        return content;
      } else {
        return content ?? null;
      }
    }
    return null;
  }
}
